import { useCallback, useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import { STAFF_CUSTOMER_DEVICES } from '@staff-app/graphql-client';
import type { StaffDevice } from '@staff-app/shared-types';
import { useStaffCustomerTeams } from '@/features/customers/hooks/useStaffCustomer';
import { useMdmDevicesFilterStore } from '../stores/mdm-devices-filter-store';
import { useMdmDevicesSortStore, type MdmDeviceSortField } from '../stores/mdm-devices-sort-store';
import {
  buildFolderOptions,
  buildTeamOptions,
  matchesFolder,
  matchesTeam,
  unknownTeamLabel,
} from '../utils/device-facets';
import { matchesDeviceQuery } from '../utils/device-search';

/**
 * Staff cross-tenant variant of `useMdmDevices` — reads via the staff-gated
 * `staffCustomerDevices` GraphQL query. Same return shape as the customer
 * hook so the COPIED UI components in `apps/staff/features/mdm-devices/`
 * render unchanged.
 *
 * See: scio3/docs/specs/19-optiedge-mdm/staff-devices-sync.md
 *   "the copied UI is kept structurally identical; only the query hook differs."
 *
 * Filter + sort are applied CLIENT-SIDE, over the FULL device set: we request
 * all of the customer's devices in one page (limit FETCH_ALL_LIMIT) so search,
 * sort, and the device-list pagination operate on every device, not just the
 * first 100. Real-time subscriptions are intentionally skipped on staff side:
 * pubsub is team-scoped and staff are cross-tenant viewers, not writers.
 *
 * Trade-off: a customer with thousands of screens fetches them all at once.
 * Fine for the single-customer staff console; revisit with true server-side
 * paging if a customer ever exceeds FETCH_ALL_LIMIT.
 */

// Effectively "all" — larger than any real single-customer device count.
const FETCH_ALL_LIMIT = 100000;

/** Local device type mirrors `MdmDevice` from apps/web so copied components typecheck. */
export type MdmDevice = StaffDevice & { fleetId?: string | null; teamName?: string | null };

interface StaffCustomerDevicesResult {
  staffCustomerDevices: {
    items: MdmDevice[];
    total: number;
  };
}

interface UseStaffCustomerDevicesOptions {
  accountId: string | undefined;
  fleetIdOverride?: string | null;
}

export function useStaffCustomerDevices(options: UseStaffCustomerDevicesOptions) {
  const { accountId, fleetIdOverride } = options;

  const searchText = useMdmDevicesFilterStore((s) => s.searchText);
  const fleetIdFromStore = useMdmDevicesFilterStore((s) => s.fleetId);
  const teamIds = useMdmDevicesFilterStore((s) => s.teamIds);
  const folderPaths = useMdmDevicesFilterStore((s) => s.folderPaths);
  const tags = useMdmDevicesFilterStore((s) => s.tags);

  const sortField = useMdmDevicesSortStore((s) => s.sortField);
  const sortDirection = useMdmDevicesSortStore((s) => s.sortDirection);

  const {
    data,
    loading,
    error,
    refetch: rawRefetch,
  } = useQuery<StaffCustomerDevicesResult>(STAFF_CUSTOMER_DEVICES, {
    variables: {
      accountId: accountId ?? '',
      pagination: { offset: 0, limit: FETCH_ALL_LIMIT },
    },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });

  // Resolve each device's team name for the (opt-in, sortable) Team column.
  // staffCustomerTeams is small + cached; devices carry only teamId.
  const { data: teamsData } = useStaffCustomerTeams(accountId);
  const teamNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of teamsData?.staffCustomerTeams ?? []) m.set(t._id, t.name);
    return m;
  }, [teamsData]);

  const allItems = useMemo<MdmDevice[]>(
    () =>
      (data?.staffCustomerDevices?.items ?? []).map((d) => ({
        ...d,
        // A teamId that no longer resolves gets a labelled fallback rather than
        // a bare Mongo id, so the Team column and the team filter agree.
        teamName: d.teamId ? (teamNameById.get(d.teamId) ?? unknownTeamLabel(d.teamId)) : null,
      })),
    [data, teamNameById],
  );
  const total = data?.staffCustomerDevices?.total ?? allItems.length;

  // Resolve effective fleet filter: prop override > store value > "all".
  const effectiveFleetFilter = fleetIdOverride !== undefined ? fleetIdOverride : fleetIdFromStore;

  const devices = useMemo<MdmDevice[]>(() => {
    let list = allItems.slice();

    if (searchText.trim()) {
      list = list.filter((d) => matchesDeviceQuery(d, searchText));
    }
    if (tags.length > 0) {
      list = list.filter((d) =>
        Array.isArray(d.tags) ? d.tags.some((tag) => tags.includes(tag)) : false,
      );
    }
    // Team and folder are independent filters, ANDed: "owned by this team AND
    // filed under this folder" is a legitimate question, and on accounts that
    // share folders across teams the answer is often empty — that's the point.
    if (teamIds.length > 0) {
      list = list.filter((d) => matchesTeam(d, teamIds));
    }
    if (folderPaths.length > 0) {
      list = list.filter((d) => matchesFolder(d, folderPaths));
    }
    if (effectiveFleetFilter !== undefined) {
      if (effectiveFleetFilter === null) {
        list = list.filter((d) => !d.fleetId);
      } else {
        list = list.filter((d) => d.fleetId === effectiveFleetFilter);
      }
    }

    // Sort in memory — match the backend's accepted fields.
    const direction = sortDirection === 'ASC' ? 1 : -1;
    list.sort((a, b) => {
      const av = pickSortable(a, sortField);
      const bv = pickSortable(b, sortField);
      if (av === bv) return 0;
      return av > bv ? direction : -direction;
    });

    return list;
  }, [allItems, searchText, tags, teamIds, folderPaths, effectiveFleetFilter, sortField, sortDirection]);

  // Facet options come from the UNFILTERED set so the pickers don't collapse as
  // you narrow. Teams additionally include every team on the account, so a team
  // that owns nothing still shows up (with a 0) instead of vanishing.
  const teamOptions = useMemo(
    () => buildTeamOptions(allItems, teamsData?.staffCustomerTeams ?? []),
    [allItems, teamsData],
  );
  const folderOptions = useMemo(() => buildFolderOptions(allItems), [allItems]);

  const refetch = useCallback(() => {
    if (!accountId) return;
    rawRefetch({ accountId, pagination: { offset: 0, limit: FETCH_ALL_LIMIT } });
  }, [rawRefetch, accountId]);

  return {
    devices,
    teamOptions,
    folderOptions,
    loading,
    error,
    totalCount: total,
    hasNextPage: false,
    loadMore: () => undefined,
    isLoadingMore: false,
    refetch,
  };
}

const lower = (v?: string | null) => (v ?? '').toLowerCase();
const time = (v: unknown) => (v ? new Date(v as string | number | Date).getTime() : 0);

/**
 * Comparable sort value for any device-table column (every column is sortable
 * client-side). Strings sort case-insensitively; date/number columns sort
 * numerically. Keys mirror the column ids in DeviceTable.
 */
function pickSortable(d: MdmDevice, field: MdmDeviceSortField): string | number {
  switch (field) {
    case 'status': return typeof d.status === 'number' ? d.status : -1;
    case 'deviceName': return lower(d.deviceName);
    case 'fleet': return lower(d.fleetId);
    case 'team': return lower(d.teamName);
    case 'path': return lower(d.path);
    case 'model': return lower(d.model);
    case 'os': return lower(d.os);
    case 'osVersion': return lower(d.osVersion);
    case 'platform': return lower(d.platform);
    case 'localAppVersion': return lower(d.localAppVersion);
    case 'assignType': return lower(d.currentType);
    case 'assignName': return lower(d.currentAssignName);
    case 'assignId': return lower(d.currentAssignId);
    case 'vpnIp': return lower(d.vpnIp);
    case 'serialNo': return lower(d.serialNo);
    case 'UUID': return lower(d.UUID);
    case 'tags': return lower(Array.isArray(d.tags) ? d.tags.join(',') : '');
    case 'tz': return lower(d.tz);
    case 'lastHeartBeat': return time(d.lastHeartBeat);
    case 'activationDate': return time(d.activationDate);
    default: return '';
  }
}
